<template>
  <div class="system-settings">
    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0 } }">
      <h3>图片存储设置</h3>
      <div class="form-group">
        <label>存储方式</label>
        <select v-model="storageType">
          <option value="local">本地存储</option>
          <option value="oss">雨云对象存储</option>
        </select>
        <p class="hint">选择图片文件的存储方式</p>
      </div>

      <!-- 雨云OSS配置 -->
      <div v-if="storageType === 'oss'" class="oss-config">
        <div class="form-group">
          <label>Endpoint（终端节点）</label>
          <input
            type="text"
            v-model="ossEndpoint"
            placeholder="例如: https://s3.cn-east-1.yun.com"
          />
          <p class="hint">雨云对象存储的访问端点地址</p>
        </div>
        <div class="form-group">
          <label>Access Key（访问密钥）</label>
          <input
            type="text"
            v-model="ossAccessKey"
            placeholder="输入 Access Key"
          />
        </div>
        <div class="form-group">
          <label>Secret Key（密钥）</label>
          <input
            type="password"
            v-model="ossSecretKey"
            placeholder="输入 Secret Key"
          />
        </div>
        <div class="form-group">
          <label>Bucket Name（存储桶名称）</label>
          <input
            type="text"
            v-model="ossBucketName"
            placeholder="例如: my-bucket"
          />
        </div>
        <div class="form-group">
          <label>Region（区域）</label>
          <input
            type="text"
            v-model="ossRegion"
            placeholder="例如: auto 或 cn-east-1"
          />
          <p class="hint">通常填写 auto 即可</p>
        </div>
        <div class="form-group">
          <label>自定义域名（可选）</label>
          <input
            type="text"
            v-model="ossCustomDomain"
            placeholder="例如: https://cdn.yourdomain.com"
          />
          <p class="hint">如果配置了自定义域名，文件将使用自定义域名访问</p>
        </div>
      </div>

      <div class="button-group">
        <button class="save-btn" @click="saveStorageSettings" :disabled="savingStorage">
          {{ savingStorage ? '保存中...' : '保存存储设置' }}
        </button>
        <button
          v-if="storageType === 'oss'"
          class="test-btn"
          @click="testOssConnection"
          :disabled="testingOss"
        >
          {{ testingOss ? '测试中...' : '测试连接' }}
        </button>
      </div>

      <!-- 测试结果显示 -->
      <div v-if="testResult" class="test-result" :class="testResult.success ? 'success' : 'error'">
        <div class="result-title">
          {{ testResult.success ? '✓ 连接成功' : '✗ 连接失败' }}
        </div>
        <div class="result-message">{{ testResult.message }}</div>
        <div v-if="testResult.details" class="result-details">
          <strong>详细信息：</strong>
          <pre>{{ testResult.details }}</pre>
        </div>
      </div>
    </div>

    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.05 } }">
      <h3>Telegram 机器人设置</h3>
      <div class="form-group">
        <label>TG Bot Token</label>
        <input
          type="text"
          v-model="tgBotToken"
          placeholder="例如: 1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
        />
        <p class="hint">从 @BotFather 获取的机器人 Token，用于接收消息通知</p>
      </div>
      <div class="form-group">
        <label>代理地址（可选，国内服务器需要）</label>
        <div class="proxy-inputs">
          <input
            type="text"
            v-model="proxyHost"
            placeholder="代理IP，如: 127.0.0.1"
            class="proxy-host"
          />
          <input
            type="text"
            v-model="proxyPort"
            placeholder="端口，如: 7890"
            class="proxy-port"
          />
        </div>
        <p class="hint">如服务器在国内无法访问 Telegram，需配置 HTTP 代理</p>
      </div>
      <button class="save-btn" @click="saveTgBotToken" :disabled="savingToken">
        {{ savingToken ? '保存中...' : '保存机器人设置' }}
      </button>
    </div>

    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.1 } }">
      <h3>Telegram 用户名设置</h3>
      <div class="form-group">
        <label>TG 用户名（用于充值跳转）</label>
        <input
          type="text"
          v-model="tgUsername"
          placeholder="例如: baicai_kefu"
        />
        <p class="hint">用户点击充值按钮后会跳转到 https://t.me/用户名</p>
      </div>
      <button class="save-btn" @click="saveTgUsername" :disabled="saving">
        {{ saving ? '保存中...' : '保存' }}
      </button>
    </div>

    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.15 } }">
      <h3>续费价格设置</h3>
      <div class="form-group">
        <label>半个月价格（元）</label>
        <input
          type="number"
          v-model="renewPriceHalfMonth"
          placeholder="45"
          min="0"
          step="0.01"
        />
      </div>
      <div class="form-group">
        <label>一个月价格（元）</label>
        <input
          type="number"
          v-model="renewPriceOneMonth"
          placeholder="90"
          min="0"
          step="0.01"
        />
      </div>
      <div class="form-group">
        <label>一年价格（元）</label>
        <input
          type="number"
          v-model="renewPriceOneYear"
          placeholder="888"
          min="0"
          step="0.01"
        />
      </div>
      <button class="save-btn" @click="saveRenewPrices" :disabled="savingPrices">
        {{ savingPrices ? '保存中...' : '保存续费价格' }}
      </button>
    </div>

    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.15 } }">
      <h3>火币充值配置</h3>
      <div class="form-group">
        <label>API Key</label>
        <input
          type="text"
          v-model="huobiApiKey"
          :placeholder="huobiApiKeyMasked ? huobiApiKeyMasked : '输入火币 API Key'"
        />
        <p class="hint" v-if="huobiApiKeyMasked">已配置（输入新值覆盖，留空保持不变）</p>
      </div>
      <div class="form-group">
        <label>Secret Key</label>
        <input
          type="password"
          v-model="huobiSecretKey"
          :placeholder="huobiSecretKeyMasked ? huobiSecretKeyMasked : '输入火币 Secret Key'"
        />
        <p class="hint" v-if="huobiSecretKeyMasked">已配置（输入新值覆盖，留空保持不变）</p>
      </div>
      <div class="form-group">
        <label>API Host（可选）</label>
        <input
          type="text"
          v-model="huobiApiHost"
          placeholder="默认: api.huobi.pro"
        />
        <p class="hint">火币API域名，一般无需修改。留空使用默认值 api.huobi.pro</p>
      </div>
      <div class="button-group">
        <button class="save-btn" @click="saveHuobiConfig" :disabled="savingHuobi">
          {{ savingHuobi ? '保存中...' : '保存火币配置' }}
        </button>
        <button class="test-btn" @click="testHuobiConfig" :disabled="testingHuobi">
          {{ testingHuobi ? '验证中...' : '测试连接' }}
        </button>
      </div>
      <div v-if="huobiTestResult" class="test-result" :class="huobiTestResult.success ? 'success' : 'error'">
        <div class="result-title">
          {{ huobiTestResult.success ? '✓ 验证成功' : '✗ 验证失败' }}
        </div>
        <div class="result-message">{{ huobiTestResult.message }}</div>
      </div>
    </div>

    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.15 } }">
      <h3>付费接口单价</h3>
      <div class="form-group">
        <label>单价（元/条）</label>
        <input
          type="number"
          v-model="paidLinkPrice"
          placeholder="1.00"
          min="0"
          step="0.01"
        />
        <p class="hint">用户购买付费防红接口时，统一按此价格扣费</p>
      </div>
      <button class="save-btn" @click="savePaidLinkPrice" :disabled="savingPaidPrice">
        {{ savingPaidPrice ? '保存中...' : '保存单价设置' }}
      </button>
    </div>

    <div class="settings-card" v-motion="{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4, delay: 0.2 } }">
      <h3>域名授权设置</h3>
      <div class="form-group">
        <label>授权验证开关</label>
        <div class="switch-container">
          <label class="switch">
            <input type="checkbox" v-model="licenseCheckEnabled" />
            <span class="slider"></span>
          </label>
          <span class="switch-label">{{ licenseCheckEnabled ? '已启用' : '已禁用' }}</span>
        </div>
        <p class="hint">
          启用后，只有在授权管理中添加的域名或IP才能启动后端服务；
          禁用后，所有域名和IP都可以启动后端服务
        </p>
      </div>
      <button class="save-btn" @click="saveLicenseSettings" :disabled="savingLicense">
        {{ savingLicense ? '保存中...' : '保存授权设置' }}
      </button>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/config/api'

export default {
  name: 'SystemSettings',
  data() {
    return {
      storageType: 'local',
      ossEndpoint: '',
      ossAccessKey: '',
      ossSecretKey: '',
      ossBucketName: '',
      ossRegion: 'auto',
      ossCustomDomain: '',
      savingStorage: false,
      testingOss: false,
      testResult: null,
      tgBotToken: '',
      proxyHost: '',
      proxyPort: '',
      savingToken: false,
      tgUsername: '',
      saving: false,
      renewPriceHalfMonth: '',
      renewPriceOneMonth: '',
      renewPriceOneYear: '',
      savingPrices: false,
      paidLinkPrice: '',
      savingPaidPrice: false,
      huobiApiKey: '',
      huobiSecretKey: '',
      huobiApiKeyMasked: '',
      huobiSecretKeyMasked: '',
      huobiApiHost: '',
      savingHuobi: false,
      testingHuobi: false,
      huobiTestResult: null,
      licenseCheckEnabled: true,
      savingLicense: false
    }
  },
  mounted() {
    this.loadSettings()
  },
  methods: {
    async loadSettings() {
      try {
        // 加载 SystemSetting 表的配置
        const res = await apiFetch('/api/settings')
        if (res.code === 0 && res.data) {
          this.storageType = res.data.storage_type || 'local'
          this.ossEndpoint = res.data.oss_endpoint || ''
          this.ossAccessKey = res.data.oss_access_key || ''
          this.ossSecretKey = res.data.oss_secret_key || ''
          this.ossBucketName = res.data.oss_bucket_name || ''
          this.ossRegion = res.data.oss_region || 'auto'
          this.ossCustomDomain = res.data.oss_custom_domain || ''
          this.tgBotToken = res.data.telegram_bot_token || ''
          this.proxyHost = res.data.telegram_proxy_host || ''
          this.proxyPort = res.data.telegram_proxy_port || ''
          this.tgUsername = res.data.tg_username || ''
          this.renewPriceHalfMonth = res.data.renew_price_half_month || ''
          this.renewPriceOneMonth = res.data.renew_price_one_month || ''
          this.renewPriceOneYear = res.data.renew_price_one_year || ''
          this.paidLinkPrice = res.data.paid_link_price || ''
          this.huobiApiKeyMasked = res.data.huobi_api_key || ''
          this.huobiSecretKeyMasked = res.data.huobi_secret_key || ''
          this.huobiApiKey = ''
          this.huobiSecretKey = ''
          this.huobiApiHost = res.data.huobi_api_host || ''
        }

        // 加载 SystemConfig 表的授权开关配置
        const configRes = await apiFetch('/api/admin/configs')
        if (configRes.code === 0 && configRes.data) {
          const licenseConfig = configRes.data.find(c => c.configKey === 'license.check.enabled')
          if (licenseConfig) {
            this.licenseCheckEnabled = licenseConfig.configValue === 'true'
          }
        }
      } catch (e) {
        console.error('加载设置失败', e)
      }
    },
    async saveStorageSettings() {
      this.savingStorage = true
      this.testResult = null // 清除之前的测试结果
      try {
        const settings = [
          { key: 'storage_type', value: this.storageType }
        ]

        if (this.storageType === 'oss') {
          settings.push(
            { key: 'oss_endpoint', value: this.ossEndpoint },
            { key: 'oss_access_key', value: this.ossAccessKey },
            { key: 'oss_secret_key', value: this.ossSecretKey },
            { key: 'oss_bucket_name', value: this.ossBucketName },
            { key: 'oss_region', value: this.ossRegion },
            { key: 'oss_custom_domain', value: this.ossCustomDomain }
          )
        }

        for (const item of settings) {
          await apiFetch('/api/settings', {
            method: 'POST',
            body: JSON.stringify(item)
          })
        }

        alert('存储设置保存成功')
      } catch (e) {
        console.error('保存失败', e)
        alert('保存失败: ' + (e.message || '未知错误'))
      } finally {
        this.savingStorage = false
      }
    },
    async testOssConnection() {
      this.testingOss = true
      this.testResult = null

      try {
        // 先保存配置（临时保存，用于测试）
        const settings = [
          { key: 'oss_endpoint', value: this.ossEndpoint },
          { key: 'oss_access_key', value: this.ossAccessKey },
          { key: 'oss_secret_key', value: this.ossSecretKey },
          { key: 'oss_bucket_name', value: this.ossBucketName },
          { key: 'oss_region', value: this.ossRegion }
        ]

        for (const item of settings) {
          await apiFetch('/api/settings', {
            method: 'POST',
            body: JSON.stringify(item)
          })
        }

        // 调用测试接口
        const res = await apiFetch('/api/admin/test-oss', {
          method: 'POST'
        })

        if (res.code === 0) {
          this.testResult = {
            success: true,
            message: '连接成功！OSS配置正确，可以正常使用。',
            details: res.data?.message || '连接测试通过'
          }
        } else {
          this.testResult = {
            success: false,
            message: '连接失败',
            details: res.msg || '未知错误'
          }
        }
      } catch (e) {
        console.error('测试失败', e)
        this.testResult = {
          success: false,
          message: '测试连接时发生错误',
          details: e.message || JSON.stringify(e)
        }
      } finally {
        this.testingOss = false
      }
    },
    async saveTgBotToken() {
      this.savingToken = true
      try {
        // 保存 Token
        await apiFetch('/api/settings', {
          method: 'POST',
          body: JSON.stringify({
            key: 'telegram_bot_token',
            value: this.tgBotToken
          })
        })
        // 保存代理设置
        await apiFetch('/api/settings', {
          method: 'POST',
          body: JSON.stringify({
            key: 'telegram_proxy_host',
            value: this.proxyHost
          })
        })
        await apiFetch('/api/settings', {
          method: 'POST',
          body: JSON.stringify({
            key: 'telegram_proxy_port',
            value: this.proxyPort
          })
        })
        alert('保存成功，机器人已重新启动')
      } catch (e) {
        console.error('保存失败', e)
        alert('保存失败')
      } finally {
        this.savingToken = false
      }
    },
    async saveTgUsername() {
      this.saving = true
      try {
        const res = await apiFetch('/api/settings', {
          method: 'POST',
          body: JSON.stringify({
            key: 'tg_username',
            value: this.tgUsername
          })
        })
        if (res.code === 0) {
          alert('保存成功')
        } else {
          alert(res.msg || '保存失败')
        }
      } catch (e) {
        console.error('保存失败', e)
        alert('保存失败')
      } finally {
        this.saving = false
      }
    },
    async saveRenewPrices() {
      this.savingPrices = true
      try {
        // 保存三个价格
        const prices = [
          { key: 'renew_price_half_month', value: this.renewPriceHalfMonth },
          { key: 'renew_price_one_month', value: this.renewPriceOneMonth },
          { key: 'renew_price_one_year', value: this.renewPriceOneYear }
        ]

        for (const item of prices) {
          if (item.value) {
            await apiFetch('/api/settings', {
              method: 'POST',
              body: JSON.stringify(item)
            })
          }
        }

        alert('续费价格保存成功')
      } catch (e) {
        console.error('保存失败', e)
        alert('保存失败')
      } finally {
        this.savingPrices = false
      }
    },
    async savePaidLinkPrice() {
      this.savingPaidPrice = true
      try {
        const res = await apiFetch('/api/settings', {
          method: 'POST',
          body: JSON.stringify({
            key: 'paid_link_price',
            value: this.paidLinkPrice
          })
        })
        if (res.code === 0) {
          alert('付费接口单价保存成功')
        } else {
          alert(res.msg || '保存失败')
        }
      } catch (e) {
        console.error('保存失败', e)
        alert('保存失败')
      } finally {
        this.savingPaidPrice = false
      }
    },
    async saveHuobiConfig() {
      this.savingHuobi = true
      try {
        const settings = []

        // 只有用户输入了新值才提交，留空表示保持不变
        if (this.huobiApiKey.trim()) {
          settings.push({ key: 'huobi_api_key', value: this.huobiApiKey.trim() })
        }
        if (this.huobiSecretKey.trim()) {
          settings.push({ key: 'huobi_secret_key', value: this.huobiSecretKey.trim() })
        }
        settings.push({ key: 'huobi_api_host', value: this.huobiApiHost.trim() })

        for (const item of settings) {
          await apiFetch('/api/settings', {
            method: 'POST',
            body: JSON.stringify(item)
          })
        }

        alert('火币配置保存成功')
        this.loadSettings() // 重新加载脱敏值
      } catch (e) {
        console.error('保存失败', e)
        alert('保存失败')
      } finally {
        this.savingHuobi = false
      }
    },
    async testHuobiConfig() {
      this.testingHuobi = true
      this.huobiTestResult = null
      try {
        const data = await apiFetch('/api/recharge/admin/verify-huobi', {
          method: 'POST'
        })
        if (data.code === 0 && data.data) {
          this.huobiTestResult = { success: true, message: data.data.message }
        } else {
          this.huobiTestResult = { success: false, message: data.msg || '验证失败' }
        }
      } catch (e) {
        this.huobiTestResult = { success: false, message: '请求失败: ' + e.message }
      } finally {
        this.testingHuobi = false
      }
    },
    async saveLicenseSettings() {
      this.savingLicense = true
      try {
        const res = await apiFetch('/api/admin/configs', {
          method: 'POST',
          body: JSON.stringify({
            configKey: 'license.check.enabled',
            configValue: this.licenseCheckEnabled ? 'true' : 'false',
            description: '域名授权验证开关'
          })
        })

        if (res.code === 0) {
          alert('授权设置保存成功！\n\n注意：修改此设置后需要重启后端服务才能生效。')
        } else {
          alert(res.msg || '保存失败')
        }
      } catch (e) {
        console.error('保存授权设置失败', e)
        alert('保存失败: ' + (e.message || '未知错误'))
      } finally {
        this.savingLicense = false
      }
    }
  }
}
</script>

<style scoped>
.system-settings { padding: 0; }
.settings-card { background: #fff; padding: 24px; border: 1px solid #e8e8e8; max-width: 500px; margin-bottom: 16px; }
.settings-card h3 { margin: 0 0 20px; font-size: 15px; font-weight: 600; color: #111; }
.form-group { margin-bottom: 16px; }
.form-group label { display: block; margin-bottom: 6px; font-size: 13px; color: #666; }
.form-group input { width: 100%; height: 38px; padding: 0 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; box-sizing: border-box; }
.form-group input:focus { border-color: #111; outline: none; }
.form-group select { width: 100%; height: 38px; padding: 0 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; box-sizing: border-box; background-color: #fff; cursor: pointer; }
.form-group select:focus { border-color: #111; outline: none; }
.oss-config { margin-top: 16px; padding-top: 16px; border-top: 1px solid #f0f0f0; }
.button-group { display: flex; gap: 8px; margin-top: 16px; }
.save-btn { background: #111; color: #fff; border: none; padding: 9px 20px; border-radius: 4px; font-size: 13px; cursor: pointer; font-weight: 500; }
.save-btn:hover { background: #333; }
.save-btn:disabled { background: #ccc; cursor: not-allowed; }
.test-btn { background: #fff; color: #333; border: 1px solid #ddd; padding: 9px 20px; border-radius: 4px; font-size: 13px; cursor: pointer; font-weight: 500; }
.test-btn:hover { border-color: #111; color: #111; }
.test-btn:disabled { background: #f5f5f5; color: #ccc; cursor: not-allowed; }
.test-result { margin-top: 16px; padding: 14px; border: 1px solid; }
.test-result.success { background-color: #fafafa; border-color: #ddd; color: #333; }
.test-result.error { background-color: #fafafa; border-color: #ddd; color: #333; }
.result-title { font-weight: 600; font-size: 14px; margin-bottom: 6px; }
.result-message { margin-bottom: 6px; font-size: 13px; }
.result-details { margin-top: 10px; padding: 10px; background: #f5f5f5; font-size: 12px; }
.result-details pre { margin: 6px 0 0; white-space: pre-wrap; word-wrap: break-word; font-family: monospace; }
.hint { margin-top: 4px; font-size: 12px; color: #999; }
.proxy-inputs { display: flex; gap: 10px; }
.proxy-host { flex: 2; }
.proxy-port { flex: 1; }
.switch-container { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ddd; transition: 0.3s; border-radius: 24px; }
.slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: #fff; transition: 0.3s; border-radius: 50%; }
input:checked + .slider { background-color: #111; }
input:checked + .slider:before { transform: translateX(20px); }
.switch-label { font-size: 13px; color: #666; }
</style>
